import React, { useContext, useCallback, useEffect, useState, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import { AlertStatus, RobotCommunicationType, RobotDefinition, RobotHealthCondition, RobotInfo } from '@moderepo/mode-apis';
import { capitalizeAll, getRobotDefinitionByRobotClass, getRobotHealthConditionName, ITEMS_PER_PAGE_10, ITEMS_PER_PAGE_LIST, PaginationBar,
    PAGING_MIN_PAGES, RCRobotsTable, RCRobotsTableProps, RCRobotsTableDataItem, SelectInputOption, useSearchParams, GenericMessageBox }
    from '@moderepo/mode-app-components';
import { fetchRobotCatalog, fetchRobots, rcDataContext, selectRobotCatalog, selectRobots } from '@moderepo/mode-data-state-robot-cloud';
import { ritzDataContext, selectProjectById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, selectSlideOutPanelOptions, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useNavigate } from 'react-router-dom';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { isRobotPreviewCompOptions, RCLinkRobotSelectorCompOptions, RCRobotPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';
import { RCRouteHelper } from './RCRouteHelper';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';



/**
 * Create an Array of Robot Class option used for filter by robots dropdown
 * @param trans
 */
const createFilterByRobotClassOptions = (
    trans: TFunction, robotCatalog: readonly RobotDefinition[] | undefined, gatewayDependentRobotsOnly?: boolean,
): readonly SelectInputOption<string | ''>[] | undefined => {
    if (robotCatalog && robotCatalog.length > 0) {
        if (gatewayDependentRobotsOnly) {
            // If gatewayDependentRobotsOnly, we will need to  filter out all robot definition that don't require gateway
            // And as for "All" option, the value will be an Array of INDIRECT robotClasses instead of "" because "" means
            // don't filter out by robotClasses. We want the backend to return only robots of INDIRECT classes.

            // Get the list of INDIRECT robot classes
            const inDirectRobotClasses = robotCatalog.filter((robotDef: RobotDefinition): boolean => {
                return robotDef.modeCommunicationType === RobotCommunicationType.GATEWAY;
            });

            // If there are more than 1 INDIRECT robots classes, create an "All" option and 1 option for each robot class
            // If 1 or less robot classes, only create "All" option
            return [{
                label: capitalizeAll(trans('label.show_all')),
                value: inDirectRobotClasses.map((robotDef: RobotDefinition): string => {
                    return robotDef.robotClass;
                }).join(','),
            }, ...(inDirectRobotClasses.length > 1
                ? inDirectRobotClasses.map((robotDef: RobotDefinition): SelectInputOption<string> => {
                    return {
                        label: robotDef.robotClass,
                        value: robotDef.robotClass,
                    };
                })
                : []),
            ];
        }

        return [{
            label: capitalizeAll(trans('label.show_all')),
            value: '',
        }, ...robotCatalog.map((robotDef: RobotDefinition): SelectInputOption<string> => {
            return {
                label: robotDef.robotClass,
                value: robotDef.robotClass,
            };
        })];
    }
    return undefined;
};



/**
 * The set of fields to be hidden from the table. By default, we will show all columns. If we need to hide some column,
 * set column to 'true'
 */
export interface RobotsTableHiddenFields {
    readonly id?: boolean;
    readonly preview?: boolean;
    readonly deviceId?: boolean;
    readonly homeId?: boolean;
    readonly name?: boolean;
    readonly robotClass?: boolean;
    readonly overrideRobotDefinition?: boolean;
    readonly availability?: boolean;
    readonly status?: boolean,
    readonly createdAt?: boolean;
    readonly updatedAt?: boolean;
}



/**
 * The RCRobotsListingContainer is used for displaying the list of robots in many places. In some place, we will allow the agent
 * to Add/Create robots. In some place, we allow the agent to Link robots to Gateways. So the user of this RCRobotsListingContainer
 * must tell the container whether to enable these features by setting these flags canLinkRobots, and canCreateRobots.
 */
interface RCRobotsListingContainerProps {
    readonly className?: string;
    readonly projectId: number;
    readonly userId?: number;
    readonly homeId?: number;
    readonly deviceId?: number;
    readonly gatewayDependentRobotsOnly: boolean;       // to show only robots that depends on gateway
    readonly title?: string;
    readonly itemsPerPage?: number;
    readonly hiddenFields?: RobotsTableHiddenFields;
    readonly canLinkRobots?: boolean;
    readonly canCreateRobots?: boolean;
    readonly filters?: {
        readonly homeId?: number;
        readonly deviceId?: number;
    };
    readonly onRobotSelected?: (robot: RobotInfo)=> void;    // OPTIONAL - Caller can capture the robotSelected event if it wants to handle the event
}



/**
 * This container display ALL Robots. We created a container for this instead of placing this code in the RobotsPage so that
 * this can be reused and shown in other pages.
 */
export const RCRobotsListingContainer = (props: RCRobotsListingContainerProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const { state:uiState, dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const [selectedRobot, setSelectedRobot] = useState<{    // A selected Robot require both homeId and robotId because robotId is NOT unique
        readonly homeId: number;
        readonly robotId: string;
    } | undefined>();
    const robotCatalog = selectRobotCatalog(rcDataState, props.projectId);
    const slideOutPanelOptions = selectSlideOutPanelOptions(uiState);
    const navigate = useNavigate();

    const {
        searchParams, onItemsPerPageChange, onPrevPage, onNextPage, onPage,
    } = useSearchParams<{
        readonly homeId?: number;
        readonly deviceId?: number;
    }>({
        searchText  : '',
        itemsPerPage: props.itemsPerPage || ITEMS_PER_PAGE_10,
        currentPage : 0,
        otherFields : {
            homeId  : props.filters?.homeId,
            deviceId: props.filters?.deviceId,
        },
    });
    const [selectedRobotClasses, setSelectedRobotClasses] = useState<string>('');



    // Select the list of Robots based on the current filters
    const robots = selectRobots(rcDataState, props.projectId, {
        pageNumber  : searchParams.currentPage,
        pageSize    : searchParams.itemsPerPage,
        homeId      : searchParams.otherFields.homeId,
        deviceId    : searchParams.otherFields.deviceId,
        robotClasses: selectedRobotClasses || undefined,
    });


    // Filtered by Robot Class options
    const robotClassFilterOptions = useMemo(() => {
        const robotClassOptions = createFilterByRobotClassOptions(trans, robotCatalog, props.gatewayDependentRobotsOnly);

        // Automatically select the first option on component init
        if (robotClassOptions && robotClassOptions.length > 0) {
            setSelectedRobotClasses(robotClassOptions[0].value);
        }

        return robotClassOptions;
    }, [trans, robotCatalog, props.gatewayDependentRobotsOnly]);



    /**
     * Fetch Robots base on the current pagination params
     */
    const fetchMoreRobots = useCallback(async () => {
        // If gatewayDependentRobotsOnly then only fetch robots if selectedRobotClass is not empty string
        if (project && (!props.gatewayDependentRobotsOnly || selectedRobotClasses)) {
            rcDataDispatch(fetchRobots(
                project.id,
                {
                    pageNumber  : searchParams.currentPage,
                    pageSize    : searchParams.itemsPerPage,
                    homeId      : searchParams.otherFields.homeId,
                    deviceId    : searchParams.otherFields.deviceId,
                    robotClasses: selectedRobotClasses || undefined,
                },
            )).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [project, props.gatewayDependentRobotsOnly, selectedRobotClasses, rcDataDispatch, searchParams.currentPage, searchParams.itemsPerPage,
        searchParams.otherFields.homeId, searchParams.otherFields.deviceId]);



    /**
     * This use effect is used for fetching more robots when the itemsPerPage or currentPage from the table pagination changed.
     */
    useEffect(() => {
        fetchMoreRobots();
    }, [dataDispatch, fetchMoreRobots]);



    /**
     * This used effect is used for loading robots on component loads or when robots are cleared
     */
    useEffect(() => {
        if (robots === undefined) {
            fetchMoreRobots();
        }
    }, [fetchMoreRobots, robots]);



    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(props.projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, props.projectId, rcDataDispatch]);


    /**
     * Localize the RobotInfo data before passing it to the RobotsTableComp for display
     */
    const localizedRobotsData = useMemo((): readonly RCRobotsTableDataItem[] | undefined => {
        const robotList: readonly RobotInfo[] | undefined = robots?.items;

        if (robotList && robotCatalog && robotList.length > 0) {
            return robotList.map((robot: RobotInfo): RCRobotsTableDataItem => {
                // find the robot's RobotDefinition from the list of robot definitions
                const robotDef = getRobotDefinitionByRobotClass(robotCatalog, robot.robotClass);

                // If the robot does not require gateway then we will assume the robot IS the device so we will show NA instead of deviceId.
                const deviceIdDisplay = robotDef && robotDef.modeCommunicationType !== RobotCommunicationType.GATEWAY
                    ? capitalizeAll(trans('label.not_applicable'))
                    : robot.deviceId?.toString() || '';


                // take the most severe alertStatus from robot's status's alertStatuses and use that as the robot's overall health status
                const status = robot.status
                    ? robot.status.alertStatuses.reduce((result: RobotHealthCondition, alertStatus: AlertStatus): RobotHealthCondition => {
                        if (alertStatus.severity > result) {
                            // The alert status has higher severity level, use it as the device's health
                            return alertStatus.severity as unknown as RobotHealthCondition;
                        }
                        return result;
                    }, RobotHealthCondition.NORMAL)
                    : RobotHealthCondition.UNKNOWN;


                return {
                    actualValue : robot,
                    displayValue: {
                        id                     : robot.id,
                        deviceId               : deviceIdDisplay,
                        homeId                 : robot.homeId.toString(),
                        name                   : robot.name || capitalizeAll(trans('label.none')),
                        robotClass             : robot.robotClass,
                        overrideRobotDefinition: robot.overrideRobotDefinition ? JSON.stringify(robot.overrideRobotDefinition) : '',
                        available              : !robot.deviceId,
                        status                 : capitalizeAll(trans(getRobotHealthConditionName(status))),
                        createdAt              : getDateTimeAndElapsedTime(robot.createdAt, trans, false),
                        updatedAt              : robot.updatedAt ? getDateTimeAndElapsedTime(robot.updatedAt, trans, false) : '',
                    },
                    status,
                    selected: selectedRobot?.homeId === robot.homeId && selectedRobot?.robotId === robot.id,
                };
            });
        }

        return undefined;
    }, [trans, robots, selectedRobot, robotCatalog]);


    // The table settings that we need to pass to the RobotsTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const robotsTableSettings = useMemo((): RCRobotsTableProps['fieldsSettings'] => {
        return {
            fields: {
                preview: {
                    label : '',      // The column doesn't need a label so we can leave it blank
                    hidden: props.hiddenFields?.preview,
                },
                id: {
                    dataItemProp: 'id',
                    label       : capitalizeAll(trans('label.id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.id,
                },
                status: {
                    dataItemProp: 'status',
                    label       : capitalizeAll(trans('label.status')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.status,
                },
                deviceId: {
                    dataItemProp: 'deviceId',
                    label       : capitalizeAll(trans('label.gateway_id')),   // We will only show "Gateway Id" for RC
                    sortable    : false,
                    hidden      : props.hiddenFields?.deviceId,
                },
                homeId: {
                    dataItemProp: 'homeId',
                    label       : capitalizeAll(trans('label.home_id')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.homeId,
                },
                robotClass: {
                    dataItemProp: 'robotClass',
                    label       : capitalizeAll(trans('label.robot_class')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.robotClass,
                },
                name: {
                    dataItemProp: 'name',
                    label       : capitalizeAll(trans('label.name')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.name,
                },
                overrideRobotDefinition: {
                    dataItemProp: 'overrideRobotDefinition',
                    label       : capitalizeAll(trans('label.robot_definition')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.overrideRobotDefinition,
                },
                availability: {
                    dataItemProp: 'availability',
                    label       : capitalizeAll(trans('label.available')),
                    sortable    : false,
                    hidden      : props.hiddenFields?.availability,
                },
                createdAt: {
                    dataItemProp: 'createdAt',
                    label       : capitalizeAll(trans('label.date')),
                    hidden      : props.hiddenFields?.createdAt,
                },
                updatedAt: {
                    dataItemProp: 'updatedAt',
                    label       : capitalizeAll(trans('label.date_updated')),
                    hidden      : props.hiddenFields?.updatedAt,
                },
            },
        };
    }, [trans, props.hiddenFields]);



    // The table pagination settings that we need to pass to the RobotTableComp. We will create and cache it instead of creating it each time we
    // re-render this container.
    const robotsTablePaginationBar = useMemo(() => {
        return (
            <PaginationBar {...{
                itemsPerPageList: ITEMS_PER_PAGE_LIST,
                itemsPerPage    : searchParams.itemsPerPage,
                pagingMinPages  : PAGING_MIN_PAGES,
                currentPage     : searchParams.currentPage,
                totalItems      : robots && robots.range ? robots.range.total : undefined,
                hasMoreItems    : (robots && robots.range
                    ? robots.range.total > searchParams.itemsPerPage * (searchParams.currentPage + 1)
                    : (!robots || robots.items.length >= searchParams.itemsPerPage)),
                texts: {
                    itemsPerPage: capitalizeAll(trans('label.paging.items_per_page', {
                        item_name: trans('label.item', { count: 2 }),
                    })),
                    pageNTemplate   : capitalizeAll(trans('label.page_n')),
                    pageNofMTemplate: capitalizeAll(trans('label.page_n_of_m')),
                },
                onItemsPerPageChange,
                onNextPage,
                onPrevPage,
                onPage,
            }}
            />
        );
    }, [trans, robots, searchParams, onItemsPerPageChange, onNextPage, onPrevPage, onPage]);



    // This useEffect is used for checking the slideOutPanelOptions to see if there is any robot preview comp selected.
    // if there is, automatically set it as the selectedRobotId
    useEffect(() => {
        if (isRobotPreviewCompOptions(slideOutPanelOptions) && slideOutPanelOptions.compProps.data.robotId) {
            setSelectedRobot({
                homeId : slideOutPanelOptions.compProps.data.homeId,
                robotId: slideOutPanelOptions.compProps.data.robotId,
            });
        } else {
            setSelectedRobot(undefined);
        }
    }, [slideOutPanelOptions]);


    /**
     * Change the selectedEntity to be the specified robotId
     */
    const changeSelectedRobot = useCallback((homeId: number, robotId: string) => {
        setSelectedRobot({ homeId, robotId });
        const options: RCRobotPreviewCompOptions = {
            location : SlideOutPanelLocation.RIGHT,
            compType : RCSlideOutPanelCompType.ROBOT_PREVIEW,
            compProps: {
                data   : { projectId: props.projectId, homeId, robotId },
                context: { userId: props.userId, homeId: homeId || props.homeId, deviceId: props.deviceId },
            },
        };
        uiDispatch(showSlideOutPanel(options));
    }, [props.projectId, props.userId, props.homeId, props.deviceId, uiDispatch]);



    /**
     * On Link robot button clicked, show the list of robots for the agent to select from
     */
    const onLinkRobotClicked = useCallback(async () => {
        if (props.homeId && props.deviceId) {
            setSelectedRobot(undefined);
            const options: RCLinkRobotSelectorCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.LINK_ROBOT_SELECTOR,
                compProps: {
                    data   : { projectId: props.projectId, homeId: props.homeId, deviceId: props.deviceId },
                    context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [props.homeId, props.deviceId, props.projectId, props.userId, uiDispatch]);


    /**
     * On robot selected, go immediately to the robot details page.
     */
    const onRobotSelected = useCallback((robot: RobotInfo) => {
        if (props.onRobotSelected) {
            props.onRobotSelected(robot);
            setSelectedRobot({ homeId: robot.homeId, robotId: robot.id });
        } else {
            RCRouteHelper.getInstance(navigate).gotoRobotPage({
                projectId: props.projectId,
                robotId  : robot.id,
                homeId   : robot.homeId,
                userId   : props.userId,
                deviceId : robot.deviceId,
            });
        }
    }, [navigate, props]);


    /**
     * On Create robot button clicked, show the Create Robot ui.
     */
    const onCreateRobotClicked = useCallback(async () => {
        if (props.homeId) {
            setSelectedRobot(undefined);
            const options: RCRobotPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.ROBOT_PREVIEW,
                compProps: {
                    data: {
                        projectId: props.projectId,
                        homeId   : props.homeId,
                    },
                    context: { userId: props.userId, homeId: props.homeId, deviceId: props.deviceId },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [props.homeId, props.userId, props.deviceId, props.projectId, uiDispatch]);



    // Handlers for issue severity filter changed event
    const onFilterByRobotClassChange = useCallback((robotClass: string | '') => {
        setSelectedRobotClasses(robotClass);
    }, []);


    // Create the filter settings to be passed to the RobotsTableComp
    const robotFiltersSettings = useMemo(() => {
        if (robotClassFilterOptions) {
            return {
                robotClass: {
                    currentValue: selectedRobotClasses,
                    label       : capitalizeAll(trans('label.robot_filter.robot_class')),
                    options     : robotClassFilterOptions,
                    onChange    : onFilterByRobotClassChange,
                },
            };
        }
        return undefined;
    }, [trans, selectedRobotClasses, onFilterByRobotClassChange, robotClassFilterOptions]);


    return (
        <>
            {project && (
                <RCRobotsTable
                    className={clsx(props.className)}
                    canLinkRobots={Boolean(props.canLinkRobots && props.deviceId)}
                    canCreateRobots={Boolean(props.canCreateRobots)}
                    listData={localizedRobotsData}
                    title={props.title || capitalizeAll(trans('label.robot', { count: 2 }))}
                    icon="icon mode-icon robot-solid"
                    filters={robotFiltersSettings}
                    onListItemSelected={onRobotSelected}
                    onPreviewItemClicked={(robot: RobotInfo) => {
                        changeSelectedRobot(robot.homeId, robot.id);
                    }}
                    onLinkRobotClicked={onLinkRobotClicked}
                    onCreateNewRobotClicked={onCreateRobotClicked}
                    texts={{
                        linkButtonLabel              : capitalizeAll(trans('label.link')),
                        addButtonLabel               : capitalizeAll(trans('label.add')),
                        linkExistingRobotsButtonLabel: capitalizeAll(trans('label.from_an_existing_robot')),
                        createNewRobotButtonLabel    : capitalizeAll(trans('label.create_new_robot')),
                    }}
                    paginationComp={robotsTablePaginationBar}
                    fieldsSettings={robotsTableSettings}
                    noDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.robot', { count: 2 })),
                            })}
                        />
                    )}
                    emptyDataMessageComp={(
                        <GenericMessageBox
                            title={trans('generic.no_data.title', {
                                term: capitalizeAll(trans('label.robot', { count: 2 })),
                            })}
                        />
                    )}
                />
            )}


            {!project && (
                <CircularProgress />
            )}

        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('RCRobotsListingContainer', RCRobotsListingContainer);
